import classNames from "classnames";
import { CSSProperties } from "react";
import { colors } from "constants/colors";
import {
  TIMELINE_NON_PULSING_STATUSES,
  TIMELINE_STATUS,
  TIMELINE_STATUS_COLOR,
  TIMELINE_UNPAID_BORDER_COLOR,
} from "constants/filingsTimeline";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { getTimelineStatus } from "utils/filingsTimeline/getTimelineStatus";
import { useTimelineContext } from "./TimelineContext";
import { TimelineFilingTooltip } from "./TimelineFilingTooltip";

const SELECTED_RING_HOLD_MS = 720;

type TimelineTrackDotProps = {
  filing: TimelineFilingRow;
  isSelected: boolean;
  isFaded: boolean;
  delayMs: number;
  onSelect: () => void;
};

export const TimelineTrackDot = ({
  filing,
  isSelected,
  isFaded,
  delayMs,
  onSelect,
}: TimelineTrackDotProps) => {
  const { isIntroAnimated } = useTimelineContext();

  const status = getTimelineStatus(filing);
  const isUnpaid = status === TIMELINE_STATUS.UNPAID;

  const dotStyle: CSSProperties = {
    animationDelay: isIntroAnimated ? `${delayMs}ms` : undefined,

    borderColor: colors.white,
    backgroundColor: isUnpaid ? colors.white : TIMELINE_STATUS_COLOR[status],
  };

  const selectedRingStyle: CSSProperties = {
    borderColor: isUnpaid
      ? TIMELINE_UNPAID_BORDER_COLOR
      : TIMELINE_STATUS_COLOR[status],
    animationDelay: `-${SELECTED_RING_HOLD_MS}ms`,
    animationPlayState: "paused",
  };

  const overdueLabel = filing.is_overdue ? ", overdue" : "";

  const isOverduePulseShown =
    filing.is_overdue &&
    !isSelected &&
    !TIMELINE_NON_PULSING_STATUSES.includes(status);

  return (
    <span
      className={classNames(
        "-t-ml-[5px] t-flex t-transition-opacity t-duration-150 first:t-ml-0 motion-reduce:t-transition-none",
        { "t-opacity-40": isFaded }
      )}
    >
      <TimelineFilingTooltip filing={filing}>
        <button
          type="button"
          onClick={onSelect}
          aria-label={`${filing.name}${overdueLabel}`}
          style={dotStyle}
          className={classNames(
            "t-relative t-box-content t-h-[11px] t-w-[11px] t-cursor-pointer t-rounded-full t-border-2 t-border-solid t-p-0 t-transition-transform t-duration-150 hover:t-scale-135 hover:t-shadow-md focus:t-outline-none focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-purple-20 motion-reduce:t-animate-none",
            { "t-shadow-md": isSelected, "t-animate-dot-pop": isIntroAnimated }
          )}
        >
          {isUnpaid && (
            <span
              className="t-pointer-events-none t-absolute t-inset-0 t-rounded-full t-border-2 t-border-dashed"
              style={{ borderColor: TIMELINE_UNPAID_BORDER_COLOR }}
            />
          )}

          {isSelected && (
            <span
              className="t-pointer-events-none t-absolute t-inset-0 t-animate-overdue-pulse t-rounded-full t-border-2 t-border-solid"
              style={selectedRingStyle}
            />
          )}

          {isOverduePulseShown && (
            <span className="t-pointer-events-none t-absolute t-inset-0 t-animate-overdue-pulse t-rounded-full t-border-2 t-border-solid t-border-red-40 motion-reduce:t-animate-none motion-reduce:t-scale-135 motion-reduce:t-opacity-50" />
          )}
        </button>
      </TimelineFilingTooltip>
    </span>
  );
};
